import { CustomListKey } from "@sp-crm/core";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContentFull, ContentHeader, ContentSubheader } from "components/layout";
import { InlineBanner } from "components/ui/inline-banner";
import { Spinner } from "components/ui/spinner";
import {
    LayoutSectionKey,
    LayoutSectionParentKey,
    useGetEntitiesQuery,
    useGetLayoutSectionsQuery,
} from "generated/graphql";
import React from "react";
import { useFeature } from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";
import { CustomListEditor } from "./custom-list-editor";
import { EntitySettings } from "./customize/entity-settings";

const HomeCareCustomFieldsContent: React.FC = () => {
    const entitiesQuery = useGetEntitiesQuery({}, stableQueryOptions());
    const homeCareLayoutQuery = useGetLayoutSectionsQuery(
        { parentKey: LayoutSectionParentKey.HomeCareLeadDetails },
        stableQueryOptions(),
    );

    if (homeCareLayoutQuery.isLoading) {
        return <Spinner />;
    }

    if (homeCareLayoutQuery.isError) {
        return (
            <InlineBanner type="error">
                Could not load home care custom fields. Try refreshing the page.
            </InlineBanner>
        );
    }

    const hasAdditionalInformationSection =
        homeCareLayoutQuery.data?.getLayoutSections?.layoutSections?.some(
            s => s.sectionKey === LayoutSectionKey.HomeCareLeadAdditionalInformation,
        );

    if (!hasAdditionalInformationSection) {
        return (
            <InlineBanner type="info">
                Home care custom fields are still being set up for this agency. Contact
                support if this persists.
            </InlineBanner>
        );
    }

    return (
        <QueryRenderer query={entitiesQuery} name="AgencySettingsHomeCare.getEntities">
            {data => (
                <EntitySettings entityName="HomeCareLead" entities={data.getEntities} />
            )}
        </QueryRenderer>
    );
};

export const AgencySettingsHomeCare: React.FC<unknown> = () => {
    const homeCareEnabled = useFeature("homeCarePrototype");

    if (!homeCareEnabled) {
        return null;
    }

    return (
        <div className="space-y-4 lg:space-y-8">
            <ContentFull>
                <ContentHeader>Home Care Client Statuses</ContentHeader>
                <ContentSubheader>
                    Customize home care client status labels, order, and categories to
                    support your home care workflow.
                </ContentSubheader>
                <CustomListEditor customListKey={CustomListKey.HomeCareLeadStatus} />
            </ContentFull>
            <ContentFull>
                <ContentHeader>Customize Home Care Client Fields</ContentHeader>
                <ContentSubheader>
                    Add custom fields that appear on every home care client&apos;s
                    overview tab.
                </ContentSubheader>
                <HomeCareCustomFieldsContent />
            </ContentFull>
        </div>
    );
};
